import * as C from "../const";
import apiClient from "./apiClient";
import { BACKEND_URL } from "../config";

let notifications = [];
let unreadCount = 0;
let isLoading = false;
let isInitialFetchDone = false;
let pollingIntervalId = null;
let inFlightFetchPromise = null;
let fetchGeneration = 0;
let activeAbortController = null;
const knownNotificationIds = new Set();
let activeToasts = [];

const subscribers = new Set();
const toastSubscribers = new Set();
const toastTimers = new Map();

const notifChannel =
  typeof window !== "undefined" && typeof window.BroadcastChannel !== "undefined"
    ? new window.BroadcastChannel(C.NOTIFICATION_BROADCAST_CHANNEL_NAME || "elelem_notification_sync")
    : null;

const getBackendUrl = () => BACKEND_URL;

const notifySubscribers = () => {
  const state = {
    notifications: [...notifications],
    unreadCount,
    isLoading,
  };
  subscribers.forEach((callback) => {
    try {
      callback(state);
    } catch (err) {
      console.error("[NotificationService] Subscriber error:", err);
    }
  });
};

const notifyToastSubscribers = () => {
  const currentToasts = [...activeToasts];
  toastSubscribers.forEach((callback) => {
    try {
      callback(currentToasts);
    } catch (err) {
      console.error("[NotificationService] Toast subscriber error:", err);
    }
  });
};

export const getNotifications = () => [...notifications];
export const getUnreadCount = () => unreadCount;
export const getIsLoading = () => isLoading;
export const getActiveToasts = () => [...activeToasts];

export const formatNotificationTime = (dateString, t, currentLanguage) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  const translate =
    typeof t === "function"
      ? t
      : (_key, def) => (typeof def === "object" ? def.defaultValue : def) || _key;

  if (diffInMinutes < 1) return translate("topbar.justNow", "Just now");
  if (diffInMinutes < 60)
    return translate("topbar.minutesAgo", {
      count: diffInMinutes,
      defaultValue: `${diffInMinutes}m ago`,
    });
  if (diffInHours < 24)
    return translate("topbar.hoursAgo", {
      count: diffInHours,
      defaultValue: `${diffInHours}h ago`,
    });
  if (diffInDays < 7)
    return translate("topbar.daysAgo", {
      count: diffInDays,
      defaultValue: `${diffInDays}d ago`,
    });
  return date.toLocaleDateString(currentLanguage || undefined);
};

export const subscribe = (callback) => {
  subscribers.add(callback);
  // Send current state on subscription
  callback({
    notifications: [...notifications],
    unreadCount,
    isLoading,
  });
  return () => {
    subscribers.delete(callback);
  };
};

export const subscribeToasts = (callback) => {
  toastSubscribers.add(callback);
  callback([...activeToasts]);
  return () => {
    toastSubscribers.delete(callback);
  };
};

const startToastTimer = (
  toastId,
  durationMs = C.NOTIFICATION_TOAST_AUTO_DISMISS_MS || 15000
) => {
  const existing = toastTimers.get(toastId);
  if (existing?.timerId) {
    clearTimeout(existing.timerId);
  }

  const startedAt = Date.now();
  const timerId = setTimeout(() => {
    dismissToast(toastId);
  }, durationMs);

  toastTimers.set(toastId, {
    timerId,
    remainingMs: durationMs,
    startedAt,
    isPaused: false,
  });
};

export const pauseToast = (toastId) => {
  const record = toastTimers.get(toastId);
  if (record && !record.isPaused) {
    if (record.timerId) {
      clearTimeout(record.timerId);
    }
    const elapsed = Date.now() - record.startedAt;
    const remainingMs = Math.max(1000, record.remainingMs - elapsed);
    toastTimers.set(toastId, {
      ...record,
      timerId: null,
      remainingMs,
      isPaused: true,
    });
  }
};

export const resumeToast = (toastId) => {
  const record = toastTimers.get(toastId);
  if (record && record.isPaused) {
    const resumeDuration = Math.max(3000, record.remainingMs);
    startToastTimer(toastId, resumeDuration);
  }
};

export const pauseAllToasts = () => {
  activeToasts.forEach((toast) => pauseToast(toast.id));
};

export const resumeAllToasts = () => {
  activeToasts.forEach((toast) => resumeToast(toast.id));
};

export const dismissToast = (toastId) => {
  const record = toastTimers.get(toastId);
  if (record?.timerId) {
    clearTimeout(record.timerId);
  }
  toastTimers.delete(toastId);
  activeToasts = activeToasts.filter((t) => t.id !== toastId);
  notifyToastSubscribers();
};

export const dismissAllToasts = () => {
  toastTimers.forEach((record) => {
    if (record?.timerId) {
      clearTimeout(record.timerId);
    }
  });
  toastTimers.clear();
  activeToasts = [];
  notifyToastSubscribers();
};

const addToast = (notification) => {
  const toastId = `toast-${notification.notification_id}`;
  if (activeToasts.some((t) => t.id === toastId)) return;

  const newToast = {
    id: toastId,
    notification_id: notification.notification_id,
    message: notification.message,
    created_at: notification.created_at,
  };

  // Keep at most 4 stacked toasts; drop oldest if exceeded
  if (activeToasts.length >= 4) {
    const oldest = activeToasts[0];
    dismissToast(oldest.id);
  }

  activeToasts = [...activeToasts, newToast];
  notifyToastSubscribers();

  startToastTimer(toastId, C.NOTIFICATION_TOAST_AUTO_DISMISS_MS || 15000);
};

export const fetchNotifications = async ({ silent = false, showToasts = true } = {}) => {
  if (inFlightFetchPromise) {
    return inFlightFetchPromise;
  }

  const currentGeneration = ++fetchGeneration;
  if (!silent) {
    isLoading = true;
    notifySubscribers();
  }

  activeAbortController =
    typeof AbortController !== "undefined" ? new AbortController() : null;

  inFlightFetchPromise = (async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await apiClient.get(`${backendUrl}/api/v1/notifications`, {
        signal: activeAbortController?.signal,
      });

      if (currentGeneration !== fetchGeneration) {
        return { notifications: [...notifications], unreadCount };
      }

      const data = response.data;
      if (currentGeneration !== fetchGeneration) {
        return { notifications: [...notifications], unreadCount };
      }

      if (data && data.success && Array.isArray(data.notifications)) {
        const fetched = data.notifications;

        if (isInitialFetchDone && showToasts) {
          const newlyDiscoveredUnread = fetched.filter(
            (n) => !n.is_seen && !knownNotificationIds.has(n.notification_id)
          );
          newlyDiscoveredUnread.forEach((n) => addToast(n));
        }

        fetched.forEach((n) => knownNotificationIds.add(n.notification_id));
        isInitialFetchDone = true;

        notifications = fetched;
        unreadCount = fetched.filter((n) => !n.is_seen).length;
      }
    } catch (error) {
      if (error?.name !== "AbortError" && error?.code !== "ERR_CANCELED") {
        console.error("Error fetching notifications:", error);
      }
    } finally {
      if (currentGeneration === fetchGeneration) {
        isLoading = false;
        inFlightFetchPromise = null;
        activeAbortController = null;
        notifySubscribers();
      }
    }
    return { notifications: [...notifications], unreadCount };
  })();

  return inFlightFetchPromise;
};

export const markAsSeen = async (notificationId) => {
  const target = notifications.find((n) => n.notification_id === notificationId);
  if (!target || target.is_seen) {
    return;
  }

  // Snapshot previous state for rollback on network failure
  const previousNotifications = [...notifications];
  const previousUnreadCount = unreadCount;

  // Optimistically update local state
  notifications = notifications.map((n) =>
    n.notification_id === notificationId
      ? { ...n, is_seen: true, seen_at: new Date().toISOString() }
      : n
  );
  unreadCount = Math.max(0, unreadCount - 1);
  dismissToast(`toast-${notificationId}`);
  notifySubscribers();

  // Broadcast optimistic read to peer tabs
  notifChannel?.postMessage({
    type: "NOTIFICATION_SEEN",
    notificationId,
  });

  try {
    const backendUrl = getBackendUrl();
    await apiClient.put(`${backendUrl}/api/v1/notifications/${notificationId}/seen`);
  } catch (error) {
    console.error("Error marking notification as seen:", error);
    // Roll back local state
    notifications = previousNotifications;
    unreadCount = previousUnreadCount;
    notifySubscribers();

    // Propagate rollback to peer tabs
    notifChannel?.postMessage({
      type: "NOTIFICATION_SEEN_ROLLBACK",
      notificationId,
    });
  }
};

export const markAllAsSeen = async () => {
  const unreadNotifications = notifications.filter((n) => !n.is_seen);
  if (unreadNotifications.length === 0) return;

  const previousNotifications = [...notifications];
  const previousUnreadCount = unreadCount;

  // Optimistic local update
  notifications = notifications.map((n) => ({
    ...n,
    is_seen: true,
    seen_at: n.seen_at || new Date().toISOString(),
  }));
  unreadCount = 0;
  dismissAllToasts();
  notifySubscribers();

  notifChannel?.postMessage({ type: "ALL_NOTIFICATIONS_SEEN" });

  try {
    const backendUrl = getBackendUrl();
    const results = await Promise.all(
      unreadNotifications.map(async (n) => {
        try {
          const res = await apiClient.put(`${backendUrl}/api/v1/notifications/${n.notification_id}/seen`);
          return { id: n.notification_id, ok: !!res };
        } catch {
          return { id: n.notification_id, ok: false };
        }
      })
    );

    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    if (failedIds.size > 0) {
      // Partially roll back un-acknowledged notifications
      notifications = notifications.map((n) =>
        failedIds.has(n.notification_id)
          ? previousNotifications.find((prev) => prev.notification_id === n.notification_id) || n
          : n
      );
      unreadCount = notifications.filter((n) => !n.is_seen).length;
      notifySubscribers();

      notifChannel?.postMessage({
        type: "NOTIFICATIONS_RECONCILE",
        notifications: [...notifications],
        unreadCount,
      });
    }
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    notifications = previousNotifications;
    unreadCount = previousUnreadCount;
    notifySubscribers();
    notifChannel?.postMessage({
      type: "NOTIFICATIONS_RECONCILE",
      notifications: [...notifications],
      unreadCount,
    });
  }
};

const handleVisibilityChange = () => {
  if (typeof document !== "undefined" && document.visibilityState === "visible") {
    fetchNotifications({ silent: true });
  }
};

export const startPolling = (intervalMs = C.NOTIFICATION_POLL_INTERVAL_MS || 60000) => {
  if (pollingIntervalId) return;

  // Initial fetch
  fetchNotifications();

  pollingIntervalId = setInterval(() => {
    if (typeof document === "undefined" || document.visibilityState === "visible") {
      fetchNotifications({ silent: true });
    }
  }, intervalMs);

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }
};

export const stopPolling = () => {
  if (pollingIntervalId) {
    clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  }
};

export const clear = () => {
  fetchGeneration++;
  if (activeAbortController) {
    try {
      activeAbortController.abort();
    } catch {
      // ignore
    }
    activeAbortController = null;
  }
  stopPolling();
  dismissAllToasts();
  notifications = [];
  unreadCount = 0;
  isLoading = false;
  isInitialFetchDone = false;
  knownNotificationIds.clear();
  inFlightFetchPromise = null;
  notifySubscribers();
};

// Cross-tab message listener
if (notifChannel) {
  notifChannel.addEventListener("message", (event) => {
    const {
      type,
      notificationId,
      notifications: remoteNotifications,
      unreadCount: remoteUnreadCount,
    } = event?.data || {};

    if (type === "NOTIFICATION_SEEN" && notificationId) {
      notifications = notifications.map((n) =>
        n.notification_id === notificationId ? { ...n, is_seen: true } : n
      );
      unreadCount = notifications.filter((n) => !n.is_seen).length;
      dismissToast(`toast-${notificationId}`);
      notifySubscribers();
    } else if (type === "NOTIFICATION_SEEN_ROLLBACK" && notificationId) {
      notifications = notifications.map((n) =>
        n.notification_id === notificationId ? { ...n, is_seen: false } : n
      );
      unreadCount = notifications.filter((n) => !n.is_seen).length;
      notifySubscribers();
    } else if (type === "ALL_NOTIFICATIONS_SEEN") {
      notifications = notifications.map((n) => ({ ...n, is_seen: true }));
      unreadCount = 0;
      dismissAllToasts();
      notifySubscribers();
    } else if (type === "NOTIFICATIONS_RECONCILE" && Array.isArray(remoteNotifications)) {
      notifications = remoteNotifications;
      unreadCount = remoteUnreadCount ?? remoteNotifications.filter((n) => !n.is_seen).length;
      notifySubscribers();
    }
  });
}
